/* eslint-disable quotes */
import { Literal, Static, Union } from "runtypes";

import { OS, TaskActionVerbMapping } from "./types";

export const LookupFileOneNoteLink =
  "https://microsoft.sharepoint-df.com/teams/o365esiweb/_layouts/15/Doc.aspx?sourcedoc={03ca6c8f-3a80-456f-a8a5-dc976684ff29}&action=edit&wd=target%28Topology%20Development.one%7C6bcef9ef-5801-4cbd-8d8a-f53f5d406c73%2FWhat%20are%20Lookup%20Files%7Cab2b9935-873f-4746-aad8-c695e06c3e4a%2F%29&wdorigin=703";

/**
 * A list of all the replaceables that do not have to be marked as required
 */
export const OPTIONAL_REPLACEABLES = [
  "Profiles",
  "ExtraProperties",
  "DotNetPreReleaseVersion",
  "FocusJobID",
  "Alias",
  "Official",
  "FocusGroupID",
  "FocusServer",
  "FocusCategory",
  "BuildSeries",
  "HoldTime",
  "SkipProfileWait",
  "SkipTestExecution",
];

/**
 * A lot of Unified Topologies list the values as things like "ControlPlaneBuildOrJobNumber"
 */
export const BUILD_OR_JOB_NUMBER = "BuildOrJobNumber";

/**
 * A regex to match BuildSourceSystem or BuildsSourceSystem
 */
export const BUILD_SOURCE_SYSTEM_REGEX = /Build(s?)SourceSystem$/i;

/**
 * A regex to match any non-digit characters: \D
 */
export const NON_DIGIT_REGEX = RegExp(/\D+/gm);

/**
 * A regex to match an official build number for Coral repository: (0|(?!0)\d{1,2})[.](0|(?!0)\d{1,2})[.](0|(?!0)\d{1,4})[.](0|(?!0)\d{1,3})
 */
export const CORAL_BUILD_REGEX = RegExp(/(0|(?!0)\d{1,2})[.](0|(?!0)\d{1,2})[.](0|(?!0)\d{1,4})[.](0|(?!0)\d{1,3})/m);

/**
 * A regex to match an official build number for Mono repository: [0-9]{2}[.][0-9]{2}[\][0-9]{4}[.][0-9]{3}
 */
export const MONO_BUILD_REGEX = RegExp(/[0-9]{2}[.][0-9]{2}[.][0-9]{4}[.][0-9]{3}/m);

// using this blue hex value since the theme blue did not have enough contrast to pass accessibility
export const ACCESSIBLE_BLUE = "#0078D4";

export const RDP_TEMPLATE =
  "screen mode id:i:2\n" +
  "use multimon:i:0\n" +
  "desktopwidth:i:1440\n" +
  "desktopheight:i:900\n" +
  "session bpp:i:32\n" +
  "winposstr:s:0,1,451,102,1897,885\n" +
  "compression:i:1\n" +
  "keyboardhook:i:2\n" +
  "audiocapturemode:i:0\n" +
  "videoplaybackmode:i:1\n" +
  "connection type:i:2\n" +
  "displayconnectionbar:i:1\n" +
  "disable wallpaper:i:1\n" +
  "allow font smoothing:i:0\n" +
  "allow desktop composition:i:0\n" +
  "disable full window drag:i:1\n" +
  "disable menu anims:i:1\n" +
  "disable themes:i:0\n" +
  "disable cursor setting:i:0\n" +
  "bitmapcachepersistenable:i:1\n" +
  "audiomode:i:0\n" +
  "redirectprinters:i:1\n" +
  "redirectcomports:i:0\n" +
  "redirectsmartcards:i:1\n" +
  "redirectclipboard:i:1\n" +
  "redirectposdevices:i:0\n" +
  "redirectdirectx:i:1\n" +
  "autoreconnection enabled:i:1\n" +
  "authentication level:i:2\n" +
  "prompt for credentials:i:0\n" +
  "negotiate security layer:i:1\n" +
  "remoteapplicationmode:i:0\n" +
  "alternate shell:s:\n" +
  "shell working directory:s:\n" +
  "gatewayusagemethod:i:\n" +
  "gatewaycredentialssource:i:\n" +
  "gatewayprofileusagemethod:i:\n" +
  "promptcredentialonce:i:0\n" +
  "use redirection server name:i:0\n" +
  "administrative session:i:1\n" +
  "drivestoredirect:s:*\n" +
  "full address:s:{0}\n" +
  "username:s:{1}\n" +
  "gatewayhostname:s:";

/**
 * The default RDP username (local administrator) used when no forest admin account applies.
 */
export const DEFAULT_RDP_USERNAME = ".\\administrator";

/**
 * The TopologyType DisplayName substring that identifies EOP topologies (e.g. "EOP VDC Mini", "EOP Mailflow").
 */
export const EOP_TOPOLOGY_IDENTIFIER = "EOP";

/**
 * The TopologyRole of the CAP-DC (forest domain controller) machine in EOP topologies.
 */
export const CAP_DC_TOPOLOGY_ROLE = "CAP-DC";

export const MATCH_NON_WORD_CHARACTERS = /[\W_]+/g;

export const OTHER_REASON_ID = 0;

export const CUSTOM = "<<CUSTOM>>";

export const CUSTOM_NOT_SUPPORTED = [
  "Not Supported",
  "Pre-builtOnly",
  "Not Implemented",
  "PreBuiltOnly",
  "TBD",
  "BlessedNotSupported",
];

export const CHECKOUT_NOT_SUPPORTED = ["CustomBuildNumberOnly", "CustomBuildOnly", "Delimiter-DoNotUse"];
export const SELECTED_THEME_COOKIE_NAME = "theme";
export const DEFAULT_THEME_NAME = "Default";
export const DARK_THEME_NAME = "Dark";
export const ThemeOptions = Union(Literal(DEFAULT_THEME_NAME), Literal(DARK_THEME_NAME));
export type IThemeOption = Static<typeof ThemeOptions>;

export const LOG_LEVEL = {
  CONSOLE_ONLY: "CONSOLE_ONLY",
  DEBUG: "DEBUG",
  ERROR: "ERROR",
};

export const GeneratedTemplateUploadStatus = {
  Uploading: "Uploading",
  UploadSucceeded: "Upload succeeded",
  UploadFailed: "Upload failed",
  NotStarted: "Not uploaded yet",
};

export const OSSINFRA_DROP_BASEURL = "https://ossinfra.artifacts.visualstudio.com";

export const OSSINFRA_DROPEXPLORER_BASEURL =
  "https://ossinfra.visualstudio.com/DefaultCollection/_apps/hub/ms-vscs-artifact.build-tasks.drop-hub-group-explorer-hub?forcelowercase=true&name=";

export const TOKEN_ERROR_MESSAGE = "Unable to get/renew token.";

export const GRAPH_API_USER_READ_SCOPE = "https://graph.microsoft.com/User.ReadBasic.All";

export const REGION_CODE_MAPPING: Record<string, string[]> = {
  "NAM": ["US & Canada", "North America"],
  "EUR": ["Europe"],
  "SEA": ["Southeast Asia"],
  "BRAS": ["Brazil South", "US & Canada", "North America"],
  "SCUS": ["South Central US", "US & Canada", "North America"],
};

export const LATEST_OS = ["Windows 2016", "Win2016", "Windows Threshold"];

export const BUILD_DROP_ROOT = "BuildDropRoot";

export const DOWNLOAD_URL = "BuildDrop";

export const BUILD_TYPE = "BuildSourceSystem";

export const FOCUS_JOB_ID = "FocusJobID";

export const BUILD_TYPES = ["Official", "Focus", "CloudTest"];

export const DOMAINS = [
  "Redmond",
  "Ntdev",
  "NorthAmerica",
  "FarEast",
  "Europe",
  "MiddleEast",
  "Africa",
  "SouthAmerica",
  "SouthPacific",
];

export const PAUSE_RESUME = {
  PAUSE: "Pause",
  RESUME: "Resume",
};

export const RDP = "RDP";

export const RDP_CORP = "CORPNET";

export const RDP_VPN = "TDSVPN";

export const BUILD_NUMBER = "BuildNumber";

export const UNAUTHORIZED_MESSAGE =
  "You are not authorized to access this site and require one of the following CoreIdentity Entitlements:\n" +
  "O365-BUILD-ARTIFACTS\n" +
  "O365-TDS-USER\n\n" +
  "Refer [//aka.ms/tdskb/permissions] for details and visit [//aka.ms/coreidentity] to obtain the appropriate entitlement.";

export const ENVIRONMENT_TYPE = ["Cloud", "Classic"];

export const MICROSOFT_DOMAIN = "microsoft.com";

export const FORM_TYPE = {
  PRIVATE_TOPOLOGY: "Private",
  LOOKUP_TOPOLOGY: "Lookup",
};

export const TOO_SHORT_OTHER_REASON_MESSAGE = "Other reason must be at least 10 characters";

export const RDP_Helper_Template =
  "@echo off\n" +
  ":: BatchGotAdmin\n" +
  ":-------------------------------------\n" +
  "REM  --> Check for permissions\n" +
  '>nul 2>&1 "%SYSTEMROOT%\\system32\\cacls.exe" "%SYSTEMROOT%\\system32\\config\\system"\n' +
  "REM --> If error flag set, we do not have admin.\n" +
  "if '%errorlevel%' NEQ '0' (\n" +
  "echo Requesting administrative privileges...\n" +
  "goto UACPrompt\n" +
  ") else ( goto gotAdmin )\n" +
  ":UACPrompt\n" +
  'echo Set UAC = CreateObject^("Shell.Application"^) > "%temp%\\getadmin.vbs"\n' +
  'set params = %*:"=""\n' +
  'echo UAC.ShellExecute "cmd.exe", "/c %~s0 %params%", "", "runas", 1 >> "%temp%\\getadmin.vbs"\n' +
  '"%temp%\\getadmin.vbs"\n' +
  'del "%temp%\\getadmin.vbs"\n' +
  "exit /B\n" +
  ":gotAdmin\n" +
  "set scriptPath=\\\\redmond.corp.microsoft.com\\exchange\\Build\\Central\\Tools\\tDSWebConnectHelper\\{scriptFileName}.ps1\n" +
  "if exist %scriptPath% (echo %scriptPath% exists.) else (echo %scriptPath% does not exist. Please connect to MSFTVPN manually. Once connected, Press any key to continue.\npause) \n" +
  "powershell -File %scriptPath% {ip:port}\n";

export const TOPOLOGY_TO_OS: Map<string, OS> = new Map([
  ["(Griffin) Datacenter Basic Griffin SQI", "Windows Threshold"],
  ["(Substrate) E15 Datacenter Basic SAFM (CustomBuildOnly)", "Windows Threshold"],
  ["(Substrate) E15 Datacenter Basic SAFM Multiple Account Forest (CustomBuildOnly)", "Windows Threshold"],
  ["(Oslo) Mserv One Box", "Windows 2012R2"],
  ["(Substrate) DatacenterBasic BPOS Cloud using BEC WindowsThreshold", "Windows Threshold"],
  ["TDF (CustomBuildOnly)", "Windows 2019 Core"],
  ["(Satchmo) Mserv Windows 2019 One Box", "Windows 2019 Core"],
  ["(AGS) Datacenter Basic Griffin AGS (CustomBuildOnly)", "Windows Threshold"],
]);

export const MSFT_SHAREPOINT = "https://microsoft.sharepoint-df.com/teams/o365esiweb";

export const EASY_ID_WARNING =
  "We‘ve detected a ‘.‘ in your current input which is usually included in a user‘s EasyID. Please confirm that you are providing their alias and not their EasyID.";

export const TASK_ACTION_VERBS: TaskActionVerbMapping = {
  reset: {
    Active: "resetting",
    Past: "reset",
    Present: "reset",
  },
  skip: {
    Active: "skipping",
    Past: "skipped",
    Present: "skip",
  },
  pause: {
    Active: "pausing",
    Past: "paused",
    Present: "pause",
  },
  resume: {
    Active: "resuming",
    Past: "resumed",
    Present: "resume",
  },
};

export const TDS_WEB_ADMINS = [
  "amiah@microsoft.com",
  "angodfrey@microsoft.com",
  "balinger@microsoft.com",
  "carolynkong@microsoft.com",
  "drapp@microsoft.com",
  "dereklal@microsoft.com",
  "heortega@microsoft.com",
  "ianma@microsoft.com",
  "robland@microsoft.com",
  "romang@microsoft.com",
  "sherrywang@microsoft.com",
];

export const STORE_PERF_ALLOWED_USERS = new Set([
  ...TDS_WEB_ADMINS,
  "adacosta@ntdev.microsoft.com",
  "anandj@microsoft.com",
  "andygo@ntdev.microsoft.com",
  "anilr@microsoft.com",
  "bafeng@microsoft.com",
  "bicgu@microsoft.com",
  "brettsh@ntdev.microsoft.com",
  "ilyatsapin@microsoft.com",
  "ivfranji@exchange.microsoft.com",
  "jetfal@microsoft.com",
  "jianketang@microsoft.com",
  "mthorp@microsoft.com",
  "sergueim@microsoft.com",
  "timwi@ntdev.microsoft.com",
  "umaira@microsoft.com",
  "vakishan@microsoft.com",
  "xiasha@microsoft.com",
  "yixingma@microsoft.com",
  "yyu@microsoft.com",
]);
